import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  ClickAwayListener,
  Divider,
  Grow,
  Paper,
  Popper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useDispatch, useSelector } from "react-redux";

import {
  useGetNotificationsQuery,
  useMarkAllAsReadMutation,
  useMarkAsReadMutation,
} from "app/store/notification/api/notificationApi";

import "./index.css";
import { NotificationItem } from "./NotificationItem";
import useNotificationSocket from "./useNotificationSocket";
import { tasksApi } from "app/store/tasks/api/taskApi";
import { useLocation } from "react-router";
import Loading from "components/common/loading/Loading";
import { DEFAULT_PAGE_SIZE } from "static/pagination";

const Notification = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const anchorRef = useRef(null);
  const sentinelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const limit = DEFAULT_PAGE_SIZE;
  const userId = useSelector((state) => state.auth?.currentUser?.userId);

  const {
    data: notificationsResponse,
    refetch: refetchNotification,
    isFetching,
  } = useGetNotificationsQuery({ page, limit, userId }, { skip: !userId });

  const accumulatedNotifications = notificationsResponse?.notifications ?? [];
  const hasMore = notificationsResponse?.hasMore ?? false;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || isFetching || activeTab === 1) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setPage((prev) => prev + 1);
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isFetching, activeTab]);

  const resetToFirstPage = useCallback(() => {
    if (page === 0) {
      refetchNotification();
    } else {
      setPage(0);
    }
  }, [page, refetchNotification]);

  const handleNotificationUpdated = useCallback(() => {
    resetToFirstPage();
    if (pathname.startsWith("/tasks/pending-approvals")) {
      dispatch(tasksApi.util.invalidateTags(["tasks"]));
    }
  }, [resetToFirstPage, pathname, dispatch]);

  useNotificationSocket({ refetchNotification: handleNotificationUpdated });

  const unreadCount = notificationsResponse?.unreadCount ?? 0;
  const unreadNotifications = accumulatedNotifications.filter((n) => !n.isRead);

  const [markAllAsRead] = useMarkAllAsReadMutation();
  const [markAsRead] = useMarkAsReadMutation();

  const displayedNotifications =
    activeTab === 0 ? accumulatedNotifications : unreadNotifications;

  const handleToggle = () => {
    setOpen((prev) => {
      if (!prev) resetToFirstPage();
      return !prev;
    });
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
    resetToFirstPage();
  };

  const handleMarkRead = async (notification) => {
    setOpen(false);
    dispatch(tasksApi.util.invalidateTags(["tasks"]));

    if (notification.isRead) return;

    await markAsRead(notification.id);
  };

  return (
    <>
      <span
        ref={anchorRef}
        className="header__profile-icon"
        onClick={handleToggle}
        style={{ marginRight: "16px" }}
      >
        <Tooltip title="Notifications" placement="bottom">
          <Badge
            badgeContent={unreadCount}
            color="secondary"
            max={99}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "10px",
                height: "16px",
                minWidth: "16px",
              },
            }}
          >
            <NotificationsIcon />
          </Badge>
        </Tooltip>
      </span>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom-start"
        modifiers={[
          {
            name: "flip",
            options: {
              fallbackPlacements: ["bottom-end"],
            },
          },
        ]}
        style={{ zIndex: 1300 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom-end" ? "top right" : "top left",
              marginTop: "8px",
            }}
          >
            <Paper elevation={4}>
              <ClickAwayListener onClickAway={handleClose}>
                <div className="notification-dropdown">
                  <div className="notification-dropdown__header">
                    <Typography className="notification-dropdown__title">
                      Notifications
                    </Typography>
                    {unreadCount > 0 && (
                      <button
                        className="notification-dropdown__mark-all"
                        onClick={handleMarkAllRead}
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>

                  <Divider />

                  <Paper
                    square
                    elevation={0}
                    style={{
                      position: "relative",
                      top: "1px",
                      zIndex: 1000,
                      backgroundColor: "transparent",
                    }}
                  >
                    <Tabs
                      value={activeTab}
                      onChange={(_, val) => setActiveTab(val)}
                      className="notification-dropdown__tabs"
                      indicatorColor="secondary"
                      TabIndicatorProps={{
                        style: { top: 0, borderBottom: "none" },
                      }}
                      textColor="secondary"
                    >
                      <Tab label="All Notifications" sx={{ fullWidth: true }} />
                      <Tab
                        label={
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            Unread
                            {unreadCount > 0 && (
                              <div style={{ marginLeft: "16px" }}>
                                <Badge
                                  badgeContent={unreadCount}
                                  color="secondary"
                                  sx={{
                                    "& .MuiBadge-badge": {
                                      fontSize: "10px",
                                      height: "16px",
                                      minWidth: "16px",
                                      padding: "0 4px",
                                    },
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        }
                        sx={{ fullWidth: true }}
                      />
                    </Tabs>
                  </Paper>

                  <div className="notification-dropdown__body">
                    {displayedNotifications.length === 0 ? (
                      <div className="notification-dropdown__empty">
                        <div className="notification-dropdown__empty-icon">
                          <NotificationsIcon />
                        </div>
                        <Typography className="notification-dropdown__empty-text">
                          <p className="notification-dropdown__empty-title">
                            No notifications !
                          </p>
                          We will let you know when there will be something to
                          update you.
                        </Typography>
                      </div>
                    ) : (
                      <>
                        {displayedNotifications.map((notification) => (
                          <NotificationItem
                            key={notification.id}
                            notification={notification}
                            onRead={handleMarkRead}
                          />
                        ))}
                        {activeTab === 0 && hasMore && !isFetching && (
                          <div ref={sentinelRef}>
                            <Loading height="60px" loadingSize={24} />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default Notification;
